import { useCallback, useEffect, useState } from 'react';
import {
  UsePaginatedQueryProps,
  UsePaginatedQueryResponseProps,
} from './props';
import { Nullable } from '../../interfaces';

/**
 * Custom hook to manage paginated queries.
 *
 * @template T - The type of the data being paginated.
 * @param {UsePaginatedQueryProps<T>} props - The properties for the paginated query hook.
 * @param {object} props.data - The data object containing metadata about pagination.
 * @param {number} props.page - The current page number.
 * @param {Function} props.setPage - Function to update the current page number.
 * @param {object} props.queryParams - The query parameters for the request.
 *
 * @returns {UsePaginatedQueryResponseProps} - An object containing functions to manage pagination.
 *
 * Example usage:
 * const { setCurrentPage, fetchNextPage, fetchPreviousPage } = usePaginatedQuery({
 *   data: { metadata: { hasNextPage: true, hasPreviousPage: false } },
 *   page: 1,
 *   setPage: (newPage) => function(newPage),
 *   queryParams: { search: 'example' },
 * });
 */
export function usePaginatedQuery<T>({
  data,
  page,
  setPage,
  queryParams,
}: UsePaginatedQueryProps<T>): UsePaginatedQueryResponseProps {
  // State to track the last set of query parameters
  const [lastQueryParams, setLastQueryParams] =
    useState<Nullable<object>>(null);

  /**
   * Function to set the current page.
   * It updates the page number if it is different from the current page.
   *
   * @param {number} newPage - The new page number to set.
   */
  const setCurrentPage = useCallback(
    (newPage: number) => {
      if (page == newPage) return;
      setPage(newPage);
    },
    [page, setPage],
  );

  /**
   * Function to fetch the next page of data.
   * It increments the page number if there is a next page available.
   */
  const fetchNextPage = useCallback(() => {
    if (!data?.metadata?.hasNextPage) return;

    setPage(old => old + 1);
  }, [data, setPage]);

  /**
   * Function to fetch the previous page of data.
   * It decrements the page number if there is a previous page available.
   */
  const fetchPreviousPage = useCallback(() => {
    if (!data?.metadata?.hasPreviousPage) return;

    setPage(old => old - 1);
  }, [data, setPage]);

  /**
   * Function to compare two objects and determine if their values are different.
   *
   * @template T - The type of the objects being compared.
   * @param {T} obj1 - The first object to compare.
   * @param {T} obj2 - The second object to compare.
   * @returns {boolean} - Returns true if the objects have different values, otherwise false.
   */
  function areObjectValuesDifferent<T>(obj1: T, obj2: T): boolean {
    for (const key of Object.keys(obj1)) {
      if (obj1[key] !== obj2[key]) {
        return true;
      }
    }

    return false;
  }

  /**
   * Function to handle changes in query parameters.
   * It resets the page to 1 if the query parameters have changed.
   */
  function onChangeQueryParams() {
    if (!queryParams) {
      return;
    }

    if (!lastQueryParams) {
      setLastQueryParams(queryParams);
      return;
    }

    if (!areObjectValuesDifferent(lastQueryParams, queryParams)) {
      return;
    }

    setLastQueryParams(queryParams);
    setPage(1);
  }

  // Effect to handle changes in query parameters
  useEffect(onChangeQueryParams, [queryParams, lastQueryParams, setPage]);

  // Return functions to manage pagination
  return {
    setCurrentPage,
    fetchNextPage,
    fetchPreviousPage,
    page,
  };
}

export type { UsePaginatedQueryProps, UsePaginatedQueryResponseProps };
